'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import BottomNav from '@/app/components/BottomNav';
import { RateStorage, TransactionStorage, SettingsStorage } from '@/lib/local-storage';

interface CalculationResult {
  amountSent: number;
  amountReceived: number;
  exchangeRate: number;
  feeAmount: number;
  feePercentage: number;
  marginPercentage: number;
  direction: string;
  totalAmount: number;
}

export default function HomePage() {
  const router = useRouter();
  const [greeting, setGreeting] = useState('Good morning,');
  const [amountSent, setAmountSent] = useState('1');
  const [direction, setDirection] = useState('GHS_KWD');
  const [calculation, setCalculation] = useState<CalculationResult | null>(null);
  
  // Rate state
  const [buyerRate, setBuyerRate] = useState<number>(0.3050);
  const [sellerRate, setSellerRate] = useState<number>(14.45);
  const [crossRate, setCrossRate] = useState<number>(() => 14.45 / 0.3050);
  const [inverseRate, setInverseRate] = useState<number>(() => 0.3050 / 14.45);
  const [loading, setLoading] = useState(true);
  const [exchangeRateText, setExchangeRateText] = useState<string>(`1 GHS = ${(0.3050 / 14.45).toFixed(4)} KWD`);
  const [serviceChargeIncluded, setServiceChargeIncluded] = useState(false);
  const SERVICE_CHARGE = 0.5;

  useEffect(() => {
    const hour = new Date().getHours();
    let newGreeting = 'Good morning,';
    
    if (hour >= 12 && hour < 18) {
      newGreeting = 'Good afternoon,';
    } else if (hour >= 18 || hour < 5) {
      newGreeting = 'Good evening,';
    }
    
    setGreeting(newGreeting);
  }, []);

  // Handle currency swap
  const handleSwap = () => {
    console.log(`[HOME] Swapping currencies from ${direction}`);
    const newDirection = direction === 'GHS_KWD' ? 'KWD_GHS' : 'GHS_KWD';
    setDirection(newDirection);
    console.log(`[HOME] New direction: ${newDirection}`);
  };

  // Load saved rates and last conversion from database (with local storage fallback)
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        console.log(`[HOME] Loading rates from database`);
        
        // Try to load rates from database first
        let ratesLoaded = false;
        try {
          const fetchUrl = `${process.env.NEXT_PUBLIC_API_URL}/api/rates/get`;
          console.log('[HOME] Fetching rates from:', fetchUrl);
          const response = await fetch(fetchUrl);
          console.log('[HOME] Rates fetch response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('[HOME] Rates data:', data);
            const rates = data.data || [];
            
            // The database only saves ONE rate at a time (the one from the currently selected tab in settings)
            // Get the first (and only) saved rate
            const savedRate = rates[0];
            
            if (savedRate) {
              console.log(`[HOME] Found saved rate from database:`, savedRate);
              console.log(`[HOME] Direction: ${savedRate.direction}, Buyer Rate: ${savedRate.buyer_rate}, Seller Rate: ${savedRate.seller_rate}`);
              
              // Use the saved rates exactly as they are
              // buyer_rate and seller_rate are the two values saved in settings
              // Convert to numbers in case they come as strings from database
              setBuyerRate(Number(savedRate.buyer_rate));
              setSellerRate(Number(savedRate.seller_rate));
              ratesLoaded = true;
            } else {
              console.log('[HOME] No rates found in response, using defaults');
            }
          } else {
            console.log('[HOME] Rates fetch failed with status:', response.status);
          }
        } catch (error) {
          let errorMsg = 'Unknown error';
          if (error instanceof Error) errorMsg = error.message;
          else if (typeof error === 'string') errorMsg = error;
          console.log('[HOME] Error fetching from database:', errorMsg);
        }

        // Fallback to local storage if database fetch failed
        if (!ratesLoaded) {
          console.log(`[HOME] Loading saved rates from local storage`);
          const kwd_ghs_rate = await RateStorage.getRate('KWD_GHS');
          const ghs_kwd_rate = await RateStorage.getRate('GHS_KWD');

          if (kwd_ghs_rate) {
            console.log(`[HOME] Found saved KWD_GHS rates:`, kwd_ghs_rate);
            setBuyerRate(kwd_ghs_rate.buyer_rate);
            setSellerRate(kwd_ghs_rate.seller_rate);
          } else if (ghs_kwd_rate) {
            console.log(`[HOME] Found saved GHS_KWD rates:`, ghs_kwd_rate);
            setBuyerRate(ghs_kwd_rate.seller_rate);
            setSellerRate(ghs_kwd_rate.buyer_rate);
          } else {
            console.log(`[HOME] No saved rates found, using defaults`);
          }
        }

        // Load service charge setting
        const serviceChargeEnabled = await SettingsStorage.getServiceChargeEnabled();
        setServiceChargeIncluded(serviceChargeEnabled);

        setLoading(false);
      } catch (error: any) {
        console.log('[HOME] Note: Using default values', error);
        setLoading(false);
      }
    };

    loadSavedData();
  }, []);

  // Calculate exchange rates based on saved rates
  useEffect(() => {
    console.log(`[HOME] Recalculating rates - buyer_rate: ${buyerRate}, seller_rate: ${sellerRate}, Direction: ${direction}`);
    
    // buyer_rate and seller_rate are direct exchange rates
    // buyer_rate = 1 KWD → X GHS (what you GET when buying)
    // seller_rate = 1 KWD → Y GHS (what you GET when selling) - typically higher for buyers
    // For this app: if direction is KWD_GHS, use buyer_rate directly
    // If direction is GHS_KWD, invert it
    
    const kwd_to_ghs = typeof buyerRate === 'number' ? buyerRate : Number(buyerRate) || 0;
    const ghs_to_kwd = kwd_to_ghs > 0 ? 1 / kwd_to_ghs : 0; // Inverse rate
    
    console.log(`[HOME] 1 KWD = ${kwd_to_ghs.toFixed(4)} GHS`);
    console.log(`[HOME] 1 GHS = ${ghs_to_kwd.toFixed(4)} KWD`);
    
    setCrossRate(kwd_to_ghs);
    setInverseRate(ghs_to_kwd);

    if (direction === 'GHS_KWD') {
      setExchangeRateText(`1 GHS = ${ghs_to_kwd.toFixed(4)} KWD`);
    } else {
      setExchangeRateText(`1 KWD = ${kwd_to_ghs.toFixed(4)} GHS`);
    }
  }, [buyerRate, sellerRate, direction]);

  // Calculate remittance whenever amount, rates, direction, or service charge changes
  useEffect(() => {
    if (!amountSent) return;
    
    let amount = parseFloat(amountSent.replace(/,/g, ''));
    if (isNaN(amount) || amount <= 0) return;

    try {
      let rawAmount = 0;
      let recipientAmount = 0;
      let rateToUse = 0;
      let sourceCurrency = '';
      let targetCurrency = '';
      let serviceChargeAmount = 0;
      
      if (serviceChargeIncluded) {
        if (direction === 'KWD_GHS') {
          serviceChargeAmount = SERVICE_CHARGE;
          amount = amount - serviceChargeAmount;
          if (amount <= 0) {
            setCalculation(null);
            return;
          }
        } else {
          const crossRate = buyerRate > 0 ? sellerRate / buyerRate : 0;
          if (crossRate === 0) return;
          serviceChargeAmount = SERVICE_CHARGE * crossRate;
          amount = amount - serviceChargeAmount;
          if (amount <= 0) {
            setCalculation(null);
            return;
          }
        }
      }
      
      const BINANCE_DEDUCTION = 0.9992;
      
      if (direction === 'GHS_KWD') {
        const crossRate = buyerRate > 0 ? sellerRate / buyerRate : 0;
        if (crossRate === 0) return;
        
        rawAmount = amount / crossRate;
        recipientAmount = rawAmount * BINANCE_DEDUCTION;
        rateToUse = buyerRate / sellerRate;
        sourceCurrency = 'GHS';
        targetCurrency = 'KWD';
      } else {
        if (buyerRate === 0) return;
        
        rawAmount = (amount / buyerRate) * sellerRate;
        recipientAmount = rawAmount * BINANCE_DEDUCTION;
        rateToUse = sellerRate / buyerRate;
        sourceCurrency = 'KWD';
        targetCurrency = 'GHS';
      }
      
      const displayRawAmount = targetCurrency === 'GHS' 
        ? rawAmount.toFixed(2)
        : rawAmount.toFixed(3);
      
      const displayRecipientAmount = targetCurrency === 'GHS'
        ? recipientAmount.toFixed(2)
        : recipientAmount.toFixed(3);
      
      console.log(`[HOME] Calculation - Original Amount: ${parseFloat(amountSent.replace(/,/g, ''))} ${sourceCurrency}, Service Charge: ${serviceChargeIncluded ? serviceChargeAmount.toFixed(3) : '0'}, Amount After Charge: ${amount} ${sourceCurrency}, Rate: ${rateToUse.toFixed(4)}, Raw: ${displayRawAmount} ${targetCurrency}, After Deduction: ${displayRecipientAmount} ${targetCurrency}`);
      
      const result: CalculationResult = {
        amountSent: parseFloat(amountSent.replace(/,/g, '')),
        amountReceived: Number(displayRecipientAmount),
        exchangeRate: rateToUse,
        feeAmount: serviceChargeIncluded ? serviceChargeAmount : 0,
        feePercentage: 0,
        marginPercentage: 0,
        totalAmount: parseFloat(amountSent.replace(/,/g, '')),
        direction,
      };
      
      (result as any).rawAmount = Number(displayRawAmount);
      (result as any).amountAfterCharge = amount;
      
      setCalculation(result);
    } catch (error) {
      console.error('[HOME] Calculation error:', error);
      setCalculation(null);
    }
  }, [amountSent, buyerRate, sellerRate, direction, serviceChargeIncluded]);

  return (
    <>
      <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white min-h-screen overflow-x-hidden overflow-y-scroll selection:bg-emerald-500/30 font-sans pt-6 pb-40 px-4 sm:px-6 w-screen">
        <div className="noise"></div>



        <div className="relative z-10">
          {/* Header */}
          <header className="w-full pb-4 sm:pb-6 flex items-center justify-between gap-3 mb-6">
            <div className="text-left">
              <h2 className="text-lg font-display font-bold tracking-wide text-white">REMIT</h2>
              <p className="text-[10px] text-emerald-400 font-medium">value for your money</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-surfaceHighlight border border-white/10 flex items-center justify-center overflow-hidden">
                <img src="https://app-cdn.appgen.com/b070c6ac-c459-4ede-84b3-095fd84fe232/assets/uploaded_1773997709856_c0wi6a.jpeg" alt="Agba Lawson" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 font-medium tracking-wide">{greeting}</p>
                <div className="flex items-center gap-1">
                  <h1 className="text-sm font-display font-semibold tracking-wide text-gradient">Agba</h1>
                  <div className="flex items-center gap-0.5 bg-emerald-500/10 px-1 py-0.5 rounded-full border border-emerald-500/20">
                    <i className="ph-bold ph-check-circle text-emerald-400 text-[8px]"></i>
                    <span className="text-[8px] font-semibold text-emerald-400">Verified</span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="w-full">
            {/* Calculator / Send Money Hero Module */}
            <section className="mb-6">
              <div className="glass-panel rounded-[2rem] p-4 relative overflow-hidden group">
                
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/[0.03] to-transparent pointer-events-none"></div>

                {/* Live Rate Indicator */}
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-medium text-slate-300">Quick Transfer</h2>
                  <div className="flex items-center gap-2 bg-surfaceHighlight/50 backdrop-blur-md px-2 py-1 rounded-full border border-white/5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-live-pulse"></span>
                    <span className="text-[10px] font-semibold text-emerald-400 tracking-wide uppercase">Live</span>
                  </div>
                </div>

                {/* Send Input */}
                <div className="bg-surfaceHighlight/40 rounded-xl p-3 border border-white/5 transition-colors focus-within:border-emerald-500/30 focus-within:bg-surfaceHighlight/60 mb-1.5">
                  <p className="text-[10px] text-slate-400 font-medium mb-0.5 uppercase tracking-wider">You Send</p>
                  <div className="flex items-center justify-between">
                    <input 
                      type="text" 
                      value={amountSent} 
                      onChange={(e) => setAmountSent(e.target.value.replace(/[^\d.]/g, '').replace(/(\\..*)\\./, '$1'))}
                      placeholder="0.00"
                      className="bg-transparent text-2xl font-display font-semibold text-white outline-none w-[60%] placeholder:text-slate-600" 
                    />
                    <button className="flex items-center gap-1.5 bg-surfaceHighlight hover:bg-white/10 transition-colors px-2 py-1 rounded-lg border border-white/10">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white ${
                        direction === 'GHS_KWD' ? 'bg-amber-700' : 'bg-blue-900'
                      }`}>{direction === 'GHS_KWD' ? 'GH' : 'KW'}</div>
                      <span className="font-display font-medium text-xs">{direction === 'GHS_KWD' ? 'GHS' : 'KWD'}</span>
                    </button>
                  </div>
                </div>

                {/* Service Charge Include Checkbox */}
                <div className="flex items-center justify-between mb-3 px-1 py-2">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="service-charge-include"
                      checked={serviceChargeIncluded}
                      onChange={(e) => setServiceChargeIncluded(e.target.checked)}
                      className="w-4 h-4 rounded cursor-pointer"
                    />
                    <label htmlFor="service-charge-include" className="text-xs font-medium text-slate-300 cursor-pointer">
                      Service Charge Include
                    </label>
                  </div>
                  {serviceChargeIncluded && calculation && (
                    <span className="text-xs font-semibold text-emerald-400">
                      {calculation && (calculation as any).amountAfterCharge !== undefined 
                        ? (calculation as any).amountAfterCharge.toFixed(2)
                        : '0.00'
                      } {direction === 'GHS_KWD' ? 'GHS' : 'KWD'}
                    </span>
                  )}
                </div>

                {/* Swap Button / Divider */}
                <div className="relative h-3 flex items-center justify-center z-10">
                  <div className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                  <button onClick={handleSwap} className="w-8 h-8 rounded-full bg-surface border border-white/10 flex items-center justify-center text-emerald-400 active:scale-90 transition-all hover:bg-surfaceHighlight z-10 shadow-lg">
                    <i className="ph-bold ph-arrows-down-up text-sm"></i>
                  </button>
                </div>

                {/* Receive Input */}
                <div className="bg-surfaceHighlight/40 rounded-xl p-3 border border-white/5 transition-colors focus-within:border-emerald-500/30 focus-within:bg-surfaceHighlight/60 mt-1.5 mb-3">
                  <p className="text-[10px] text-slate-400 font-medium mb-0.5 uppercase tracking-wider">Recipient Gets</p>
                  <div className="flex items-center justify-between">
                    <input 
                      type="text" 
                      readOnly
                      value={calculation ? calculation.amountReceived.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'} 
                      className="bg-transparent text-2xl font-display font-semibold text-emerald-400 outline-none w-[60%] placeholder:text-slate-600"
                    />
                    <button className="flex items-center gap-1.5 bg-surfaceHighlight hover:bg-white/10 transition-colors px-2 py-1 rounded-lg border border-white/10">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white ${
                        direction === 'GHS_KWD' ? 'bg-blue-900' : 'bg-amber-700'
                      }`}>{direction === 'GHS_KWD' ? 'KW' : 'GH'}</div>
                      <span className="font-display font-medium text-xs">{direction === 'GHS_KWD' ? 'KWD' : 'GHS'}</span>
                    </button>
                  </div>
                  {calculation && (calculation as any).rawAmount !== undefined && (
                    <div className="mt-1.5 pt-1.5 border-t border-white/5">
                      <p className="text-[9px] text-slate-500">
                        Before deduction: {((calculation as any).rawAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {direction === 'GHS_KWD' ? 'KWD' : 'GHS'}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                    <span className="text-[9px] text-slate-400">Rate</span>
                    <span className="text-[9px] font-medium text-white">
                      {exchangeRateText || 'Loading...'}
                    </span>
                  </div>
                </div>

                {/* Primary CTA */}
                <button 
                  disabled={!calculation}
                  onClick={() => {
                    if (calculation) {
                      const targetCurrency = direction === 'GHS_KWD' ? 'KWD' : 'GHS';
                      const originalAmount = parseFloat(amountSent.replace(/,/g, ''));
                      const recipientAmount = calculation.amountReceived;
                      
                      const params = new URLSearchParams({
                        amountSend: originalAmount.toString(),
                        amountReceived: recipientAmount.toString(),
                        currency: targetCurrency,
                        serviceChargeEnabled: serviceChargeIncluded.toString(),
                      });
                      router.push(`/recipients?${params.toString()}`);
                    }
                  }}
                  className="w-full py-4 rounded-xl btn-gradient text-white font-display font-semibold text-base flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-[0_8px_16px_-6px_rgba(16,185,129,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Proceed to Recipients
                  <i className="ph-bold ph-arrow-right"></i>
                </button>
              </div>
            </section>


          </main>
        </div>
      </div>
      <BottomNav />
    </>
  );
}
